import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type AppLang = 'en' | 'es' | 'pt';

const STORAGE_KEY = 'wp_lang';
const SUPPORTED: readonly AppLang[] = ['en', 'es', 'pt'] as const;

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);

  readonly supported = SUPPORTED;

  init(): void {
    this.translate.addLangs([...SUPPORTED]);
    // v17 terminology: "fallback language"
    this.translate.setFallbackLang('en');
    this.use(this.getSavedOrDefault());
  }

  current(): AppLang {
    const current = (this.translate.currentLang || this.translate.getDefaultLang() || 'en') as AppLang;
    return this.isSupported(current) ? current : 'en';
  }

  use(lang: AppLang): void {
    if (!this.isSupported(lang)) return;
    localStorage.setItem(STORAGE_KEY, lang);
    this.translate.use(lang);
    document.documentElement.lang = lang;
  }

  private getSavedOrDefault(): AppLang {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && this.isSupported(saved)) return saved;
    return 'en';
  }

  private isSupported(lang: string): lang is AppLang {
    return (SUPPORTED as readonly string[]).includes(lang);
  }
}

